<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		children: Snippet;
		/** Stack buttons in a column (e.g. table tabs down the left edge) */
		vertical?: boolean;
		/** Equal-width buttons; otherwise each sizes to its label. */
		buttonWidth?: string;
	}

	const { children, vertical = false, buttonWidth }: Props = $props();
</script>

<div class="container" class:vertical style:--module-button-width={buttonWidth}>
	{@render children()}
</div>

<style>
	/* Grouped buttons round only their outer corners. */
	.container {
		display: flex;
		flex-direction: row;
		gap: 2px;

		> :global(button) {
			border-radius: 0;
			width: var(--module-button-width, auto);
		}

		> :global(button:first-child) {
			border-start-start-radius: var(--radius);
			border-end-start-radius: var(--radius);
		}

		> :global(button:last-child) {
			border-start-end-radius: var(--radius);
			border-end-end-radius: var(--radius);
		}

		/* An expanded button's panel hangs off its bottom edge, so that edge is square. */
		> :global(button[aria-expanded='true']) {
			border-end-start-radius: 0;
			border-end-end-radius: 0;
		}

		&.vertical {
			flex-direction: column;

			> :global(button) {
				justify-content: flex-start;
				border-radius: 0;
				padding-inline: var(--space-4);
				text-align: start;
			}

			> :global(button:first-child) {
				border-start-start-radius: var(--radius);
				border-start-end-radius: var(--radius);
			}

			> :global(button:last-child) {
				border-end-start-radius: var(--radius);
				border-end-end-radius: var(--radius);
			}

			/* In a column the selection mark sits on the leading edge instead of underneath. */
			> :global(button[aria-pressed='true']::after) {
				top: var(--space-2);
				right: auto;
				bottom: var(--space-2);
				left: -7px;
				width: 3px;
				height: auto;
			}
		}
	}
</style>
